import {
  createRouter,
  createWebHistory,
  RouteLocationNormalized,
} from 'vue-router';
import { useAuthStore } from '@/store/authStore';
import { useFeatureFlagStore } from '@/store/featureFlagStore';
import { useProfileStore } from '@/store/profileStore';
import { useUiStore } from '@/store/uiStore';
import { companyRoutes } from './company-routes';
import { dashboardRoutes } from './dashboard-routes';
import { libraryRoutes } from './library-routes';
import { projectRoutes } from './project-routes';
import { roadmapRoutes } from './roadmap-routes';
import { routes as templateRoutes } from './template-routes';
import { userRoutes } from './user-routes';
import { viewsRoutes } from './views-routes';

export const router = createRouter({
  history: createWebHistory(),
  routes: [
    ...dashboardRoutes,
    {
      path: '/login',
      name: 'Login',
      meta: { hideBreadcrumbs: true, hideSideNavigation: true },
      component: () => import('@/pages/user/auth/LoginPage.vue'),
    },
    {
      path: '/auth/callback',
      name: 'AuthCallback',
      meta: { hideBreadcrumbs: true, hideSideNavigation: true },
      component: () => import('@/pages/user/auth/AuthCallbackPage.vue'),
    },
    ...companyRoutes,
    ...userRoutes,
    ...templateRoutes,
    ...projectRoutes,
    ...libraryRoutes,
    ...roadmapRoutes,
    ...viewsRoutes,
    {
      path: '/meeting-transcripts',
      name: 'MeetingTranscripts',
      component: () => import('@/app/transcripts/MeetingTranscripts.vue'),
    },
    {
      path: '/meeting-transcripts/summaries',
      name: 'MeetingSummaries',
      component: () => import('@/app/transcripts/MeetingTranscripts.vue'),
    },
    {
      path: '/meeting-transcripts/summaries/:summaryId',
      name: 'MeetingSummaryDetail',
      component: () => import('@/app/transcripts/MeetingTranscripts.vue'),
    },
    {
      path: '/feature-ranking',
      name: 'FeatureRanking',
      component: () => import('@/app/feature-ranking/FeatureRankingPage.vue'),
    },
    // Dev-only kit gallery for the design screenshots; not in production builds.
    ...(import.meta.env.DEV
      ? [
          {
            path: '/dev/kit',
            name: 'KitGallery',
            meta: { hideBreadcrumbs: true },
            component: () => import('@/pages/dev/KitGalleryPage.vue'),
          },
        ]
      : []),
    {
      path: '/:pathMatch(.*)*',
      name: 'NotFound',
      meta: { hideBreadcrumbs: true },
      component: () => import('@/ui/UiNotFoundPage.vue'),
    },
  ],
});

router.beforeEach(async (to: RouteLocationNormalized) => {
  const authStore = useAuthStore();
  const uiStore = useUiStore();

  if (uiStore.pendingOperation) {
    if (uiStore.isContextFormLoading) {
      useAlerts().warning($t('message.savingChanges'));

      return false;
    } else {
      const confirmed = await uiStore.confirmUnlockUi();
      if (confirmed === 0) {
        return false;
      }
    }
  }

  const publicRoutes = [
    'ResetPassword',
    'ResetPasswordConfirm',
    'SetNewNewPassword',
    'SetNewPasswordConfirm',
    'AuthCallback',
  ];
  if (publicRoutes.includes(to.name as string)) {
    return true;
  }

  if (
    to.name !== 'Login' &&
    !authStore.validBearer() &&
    !authStore.canRefresh()
  ) {
    return {
      name: 'Login',
      query: { redirect: to.fullPath },
    };
  }

  // Check if MeetingTranscripts route requires feature flags
  const meetingTranscriptRoutes = [
    'MeetingTranscripts',
    'MeetingSummaries',
    'MeetingSummaryDetail',
  ];
  if (meetingTranscriptRoutes.includes(to.name as string)) {
    const featureFlagStore = useFeatureFlagStore();
    const profileStore = useProfileStore();
    // The allowlist part of the check reads the user's email, so the profile
    // has to be there before we decide.
    if (!profileStore.profile.email) {
      await profileStore.loadProfile();
    }

    // Requires the AI feature flag, the Todo integration, and — where the
    // tenant configured one — membership of the meeting-transcripts allowlist.
    if (!featureFlagStore.canAccessMeetingTranscripts) {
      useAlerts().error($t('message.featureNotAvailable'));

      return { name: 'Dashboard' };
    }
  }

  // Check if Views route requires feature flags
  const viewsRouteNames = ['ViewsList', 'ViewResults', 'ManagedViewResults'];
  if (viewsRouteNames.includes(to.name as string)) {
    const profileStore = useProfileStore();
    if (!profileStore.profile.email) {
      await profileStore.loadProfile();
    }
    if (!useFeatureFlagStore().hasViews) {
      return { name: 'Dashboard' };
    }
  }

  // Check if Feature Ranking route requires role
  if (to.name === 'FeatureRanking') {
    const profileStore = useProfileStore();
    if (!profileStore.profile.email) {
      await profileStore.loadProfile();
    }
    const { canRankFeatures } = await import('@beeboard/common');
    if (!canRankFeatures(profileStore.profile.roles)) {
      return { name: 'Dashboard' };
    }
  }

  return true;
});

// Critical: Catch navigation errors that might be swallowed
router.onError((error) => {
  console.error('[Router] CRITICAL: Router error', error);
});
